import {
  LinkingOptions,
  NavigationProp,
  ParamListBase,
} from '@react-navigation/native';
import {
  CardStyleInterpolators,
  StackCardInterpolationProps,
  StackNavigationOptions,
} from '@react-navigation/stack';
import { theme } from 'aimo-ui';
import { ButtonTypes } from 'aimo-ui/lib/typescript/src/components/Button/types';
import {
  Dimensions,
  DimensionValue,
  Easing,
  LayoutRectangle,
  StyleSheet,
} from 'react-native';
import {
  featureCountryCode,
  futureFeatureCountryCode,
} from 'states/persistInStorage';
import { usePaymentStore } from 'stores/paymentStore';
import { useUiStore } from 'stores/uiStore';
import { ParkStackParamList, RootStackParamList } from 'types/navigation';
import { isWeb } from 'utils/deviceUtils';
import { useAuthStore } from '../stores/authStore';
import { useWalletTokenVerifyStore } from '../stores/walletTokenVerifyStore';

export type ScreenArrayProps = {
  screenName: string;
  tabLocalizationKey: string;
  iconName: string;
  iconNameActive: string;
  initialParams?: InitialParamProps;
  paramsString?: string;
  screenTitle?: string;
};

type InitialParamProps = {
  parkingZoneUid?: string;
  token?: string;
};

type Route = keyof ParkStackParamList | keyof RootStackParamList;

export type NavigationSettingsType = {
  initialRoute: string;
  activeTintColor: string;
  inactiveTintColor: string;
  screenArray: ScreenArrayProps[];
  linking?: LinkingOptions<RootStackParamList>;
};

const { colors } = theme;
export const WEB_MENU_BAR_HEIGHT = 80;

export const devScreen: ScreenArrayProps = {
  screenName: 'devScreen',
  screenTitle: 'Dev',
  tabLocalizationKey: 'navigation.dev',
  iconName: 'Stop-Outlined',
  iconNameActive: 'Stop-Filled',
};

export const navTestingSettings: NavigationSettingsType = {
  initialRoute: 'Services',
  inactiveTintColor: colors.gray300,
  activeTintColor: colors.black,
  screenArray: [
    {
      screenName: 'servicesScreen',
      tabLocalizationKey: 'navigation.services',
      iconName: 'Services-Outlined',
      iconNameActive: 'Service-Filled',
    },
    {
      screenName: 'accountScreen',
      tabLocalizationKey: 'navigation.account',
      iconName: 'Account-Outlined',
      iconNameActive: 'Account-Filled',
    },
  ],
};

export const navTestingAccountSettings: NavigationSettingsType = {
  initialRoute: 'Account',
  inactiveTintColor: colors.gray300,
  activeTintColor: colors.black,
  screenArray: [
    {
      screenName: 'accountScreen',
      tabLocalizationKey: 'navigation.account',
      iconName: 'Account-Outlined',
      iconNameActive: 'Account-Filled',
    },
  ],
};

export const commonNavigationSettings = {
  initialRoute: 'parkScreen',
  inactiveTintColor: colors.gray300,
  activeTintColor: colors.black,
  screenArray: [
    {
      screenName: 'parkScreen',
      screenTitle: 'Parking',
      tabLocalizationKey: 'navigation.park',
      iconName: 'Park-Outlined',
      iconNameActive: 'Park-Filled',
    },
    {
      screenName: 'rideScreen',
      screenTitle: 'Car sharing',
      tabLocalizationKey: 'navigation.ride',
      iconName: 'Car-Outlined',
      iconNameActive: 'Car-Filled',
    },
    {
      screenName: 'servicesScreen',
      screenTitle: 'Services',
      tabLocalizationKey: 'navigation.services',
      iconName: 'Services-Outlined',
      iconNameActive: 'Service-Filled',
    },
    {
      screenName: 'accountScreen',
      screenTitle: 'My account',
      tabLocalizationKey: 'navigation.account',
      iconName: 'Account-Outlined',
      iconNameActive: 'Account-Filled',
    },
  ],
  linking: {
    prefixes: [
      'aimoone://',
      'com.aimoone://',
      'https://aimo-one*.io',
      'https://aimoapp*.io',
      'http://localhost:3000',
    ],
    config: {
      screens: {
        mainTabs: {
          initialRouteName: 'parkScreen',
          screens: {
            parkScreen: {
              screens: {
                parkMain: 'park',
                parkDetail: 'park/:parkingZoneUid',
                parkInfo: 'park/:parkingZoneUid/info',
                parkEndTime: 'park/:parkingZoneUid/select-end-time',
                parkResume: 'park/:parkingZoneUid/resume-parking',
                parkPrepaidTicketsAndPermitsList:
                  'park/:parkingZoneUid/prepaid-tickets-and-permits',
                parkPrepaidTicketsAndPermitsBuy:
                  'park/:parkingZoneUid/prepaid-tickets-and-permits/:id/buy',
                parkPermitsBuy: 'park/:parkingZoneUid/permits/:pmcId/buy',
                parkPrepaidTicketsAndPermitsEdit:
                  'park/:parkingZoneUid/prepaid-tickets-and-permits/:id/edit',
                chargeOptions: 'park/:parkingZoneUid/charging-options',
                chargeSelect: 'park/:parkingZoneUid/ev-charge',
                parkingTariffs: 'park/parking-tariffs',
                chargeConnection:
                  'park/:parkingZoneUid/ev-charge/:chargingStationId/wait-connection',
                permitPurchaseCompleted: 'park/purchase-completed',
              },
            },
            rideScreen: 'ride',
            servicesScreen: {
              screens: {
                servicesMain: 'services',
                prepaidTickets: 'services/prepaidTickets',
                prepaidTicket: 'services/prepaidTickets/:ticketId',
                prepaidEdit: 'services/prepaidTickets/:ticketId/edit',
                hslTicketRender: 'services/hslTicketRender',
              },
            },
            accountScreen: {
              screens: {
                accountMain: 'account/main',
                contactUs: 'account/contact-us',
                tutorials: 'account/tutorials',
                benefits: 'account/benefits',
                myVehicles: 'account/my-vehicles',
                addVehicleAccount: 'account/add-vehicle',
                companyPermit: 'account/company-benefits',
                editVehicle: 'account/edit-vehicle',
                myPaymentMethods: 'account/my-payment-methods',
                parkingHistory: 'account/parking-history',
                permit: 'account/edit-vehicle/permits/:permitId',
                permitEdit: 'account/edit-vehicle/permits/:permitId/edit',
                prepaidHistory: 'account/prepaid-history',
                prepaidHistoryDetails:
                  'account/prepaid-history/:transactionId/:ticketId',
                permitHistoryDetails:
                  'account/prepaid-history/:transactionId/:permitId',
                parkingHistoryDetails: {
                  path: 'account/parking-history/details/:parkingZoneUid?/:sessionId?/:transactionId?',
                  parse: {
                    parkingZoneUid: () => '',
                    sessionId: () => '',
                    transactionId: () => '',
                  },
                  stringify: {
                    parkingZoneUid: () => '',
                    sessionId: () => '',
                    transactionId: () => '',
                  },
                },
                chargingHistoryDetails: {
                  path: 'account/charging-history/details/:parkingZoneUid?/:transactionId/',
                  parse: {
                    parkingZoneUid: (parkingZoneUid?: string) =>
                      parkingZoneUid || '',
                    transactionId: (transactionId?: string) =>
                      transactionId || '',
                  },
                  stringify: {
                    parkingZoneUid: (parkingZoneUid?: string) =>
                      parkingZoneUid || '',
                    transactionId: (transactionId?: string) =>
                      transactionId || '',
                  },
                },
                viewTerms: 'account/view-terms',
                aimoSubscription: 'account/aimo-subscription',
                mifareAndRfid: 'account/mifare-and-rfid',
                contactInfo: 'account/contact-info',
                notifications: 'account/notifications',
                chatbot: 'account/chatbot',
              },
            },
            devScreen: 'developer',
          },
        },
        onboardingModal: {
          screens: {
            welcome: 'onboarding/welcome',
            addPaymentMethod: 'onboarding/add-payment-method',
            addCardOnboarding: 'onboarding/add-card',
            addCardDetailsOnboarding: 'onboarding/add-card-details',
            addVehicleOnboarding: 'onboarding/add-vehicle',
            pushNotifications: 'onboarding/push-notifications',
            onboardingContinue: 'onboarding/continue',
          },
        },
        parkingReceipt: {
          path: 'parking-receipt/:sessionId',
          parse: {
            sessionId: (sessionId?: string) => sessionId || '',
          },
          stringify: {
            sessionId: (sessionId?: string) => sessionId || '',
          },
        },
        permits: 'permits',
        addVehicleParking: 'add-vehicle-parking',
        vehicleCameraRecognitionParking: 'vehicle-camera-recognition-parking',
        approveTermsAppLocation: 'approve-terms-location',
        longTermParking: {
          initialRouteName: 'mapScreen',
          path: 'long-term-parking',
          screens: {
            searchScreen: '/search',
            permitZone: '/permit-zone/:parkingZoneUid',
            buyScreen: '/buy-permit/:parkingZoneUid/:pmcId',
          },
        },
        parkingSpaceMapViewer: {
          path: 'reserve-parking-space',
        },
        cityParking: {
          initialRouteName: 'cityPmcs',
          path: 'city-parking',
          screens: {
            purchase: {
              path: '/purchase/:pmcId',
            },
            apply: {
              path: '/apply',
            },
          },
        },
        hslTicket: {
          initialRouteName: 'info',
          path: 'hsl-ticket',
          screens: {
            info: {
              path: '/:token/info',
              parse: {
                token: (token?: string) => token || '',
              },
              stringify: {
                token: (token?: string) => token || '',
              },
            },
          },
        },
        salesOrderPermitDetails: 'permit/sales-order/:orderLineIdWithVersion',
        salesOrderPermitEdit: 'permit/sales-order/edit/:orderLineIdWithVersion',
        permit: 'permit/:permitId',
        multiPoolingPermit: 'permit/multi-pooling-permit/:permitId',
        poolingGroup: 'permit/multi-pooling-permit/:permitId/:poolingGroupUid',
        permitVehicles: 'permit/:permitId/vehicles',
        approveTerms: 'account/approve-terms',
        updateAvailable: 'update-available',
        approveUpdatedTerms: 'approve-updated-terms',
        landingScreen: 'landing',
        loginSuccess: 'loginSuccess',
        // WEB-only Adyen 3DS2 redirect return route (CPS-26589 B-T8). The route
        // is registered only in navigation/index.web.tsx; native never matches
        // this path (3DS2 returns in-app via the custom scheme), so the entry is
        // inert there. The redirectResult arrives as a ?redirectResult= query
        // param, read off the URL by the screen rather than via route params.
        adyenRedirect: 'adyen-redirect',
      },
    },
  } as LinkingOptions<RootStackParamList>,
};

const getRootNavigator = (
  navigation: NavigationProp<ParamListBase>
): NavigationProp<ParamListBase> => {
  const parentState = navigation.getParent();
  if (parentState) {
    return getRootNavigator(parentState);
  }
  return navigation;
};

const isRoute = (route: Route, navigation: NavigationProp<any>) => {
  const rootNavigationState = getRootNavigator(navigation);
  const routes = rootNavigationState.getState().routes;
  return !!routes?.some((r) => r.name === route);
};

export const isOnboardingOpen = (navigation: NavigationProp<any>) => {
  return isRoute('onboardingModal', navigation);
};

export const isParkZoneDetail = (navigation: NavigationProp<any>) => {
  return isRoute('parkDetail', navigation);
};

export const isParkZoneChooseEndTime = (navigation: NavigationProp<any>) => {
  return isRoute('parkEndTime', navigation);
};

export const isParkZoneResume = (navigation: NavigationProp<any>) => {
  return isRoute('parkResume', navigation);
};

export const isLTPNavigator = (navigation: NavigationProp<any>) => {
  return isRoute('longTermParking', navigation);
};

export const beforeRemoveApproveTerms = () => {
  if (useAuthStore.getState().acceptedTermsAfterFeatureCountryChange) {
    featureCountryCode(futureFeatureCountryCode());
    useAuthStore.getState().setAcceptedTermsAfterFeatureCountryChange(true);
  } else {
    futureFeatureCountryCode(null);
  }
  useUiStore.getState().setIsSignUpFlow(false);
  useUiStore.getState().setIsApproveTermsOpen(false);
};

export const onFocusApproveTerms = () => {
  useUiStore.getState().setIsApproveTermsOpen(true);
};

const defaultPercentage = 100;

export const getModalOptions = (
  modalHeight: `${number}%` = `${defaultPercentage}%`,
  showHandle = false,
  backButtonType: ButtonTypes = 'tertiary',
  openDelay = 0,
  duration = 600,
  gestureEnabled = true
) => {
  const parsedPercentage = parseInt(modalHeight.replace('%', ''), 10);
  const numberPercentage = Math.min(
    Math.max(
      parsedPercentage,
      theme.normalising.heightPercentage(parsedPercentage)
    ),
    defaultPercentage
  );

  const top: DimensionValue = `${100 - numberPercentage}%`;
  const maxHeight: DimensionValue = `${numberPercentage}%`;

  return {
    ...StyleSheet.create({
      cardStyle: {
        top,
        maxHeight,
        borderTopLeftRadius: 40,
        borderTopRightRadius: 40,
        backgroundColor: theme.colors.white,
        marginTop: isWeb ? WEB_MENU_BAR_HEIGHT : 0,
      },
    }),
    headerStyle: {
      showHandle,
      backButtonType,
    },
    screenOptions: {
      presentation: 'transparentModal',
      transitionSpec: {
        open: {
          config: {
            delay: openDelay,
            duration,
          },
          animation: 'timing',
        },
        close: { config: { duration: 800 }, animation: 'timing' },
      },
      gestureEnabled,
      gestureResponseDistance: 1000,
      cardOverlayEnabled: true,
      detachPreviousScreen: false,
      cardStyleInterpolator: (props) => ({
        ...CardStyleInterpolators.forModalPresentationIOS(props),
        ...customCardStyleInterpolator(props),
      }),
    } as StackNavigationOptions,
  };
};

export const transitionOptions = ({
  openDuration = 400,
  closeDuration = 400,
}: {
  openDuration?: number;
  closeDuration?: number;
} = {}): StackNavigationOptions => {
  return {
    cardStyleInterpolator: (props) => ({
      ...CardStyleInterpolators.forHorizontalIOS(props),
      ...customCardStyleInterpolator(props),
    }),
    transitionSpec: {
      open: {
        animation: 'timing',
        config: {
          delay: 10,
          duration: openDuration,
          easing: Easing.bezier(0.2, 0.0, 0.2, 1),
        },
      },
      close: {
        animation: 'timing',
        config: {
          duration: closeDuration,
          easing: Easing.bezier(0.2, 0.0, 0.2, 1),
        },
      },
    },
  };
};

export const setDynamicModalHeight = (
  layout: LayoutRectangle,
  navigation: NavigationProp<any>,
  headerHeight: number
) => {
  const { height } = layout || {};
  const screenHeight = Dimensions.get('window').height;
  const percentage = ((height + headerHeight) / screenHeight) * 100;

  navigation.setOptions({
    cardStyle: {
      top: 100 - percentage + '%',
      maxHeight: percentage + '%',
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      backgroundColor: theme.colors.white,
    },
  });
};

export const customCardStyleInterpolator = (
  props: StackCardInterpolationProps
) => {
  return {
    overlayStyle: {
      opacity: props.current.progress.interpolate({
        inputRange: [0, 1],
        outputRange: [0, isWeb ? 0.2 : 0.5],
        extrapolate: 'clamp',
      }),
    },
  };
};

export const handleMobilePayVerification = (
  userId: string,
  status: string,
  paymentMethod: string
) => {
  const { setWalletTokenPendingVerification } =
    useWalletTokenVerifyStore.getState();
  setWalletTokenPendingVerification({ userId, status: status, paymentMethod });
};

export const handleMobilePayCancellation = (
  paymentMethod: string,
  paymentProvider: string
) => {
  const { setWalletTokenPendingDeletion } =
    useWalletTokenVerifyStore.getState();
  setWalletTokenPendingDeletion({ paymentMethod, paymentProvider });
};

const extractPaymentMethod = (url: string): string | null => {
  // Handle new URL format: com.aimoone:/AIMO_ONE_FI/MOBILEPAY?...
  // Extract the last path segment before query params
  const pathPart = url.split('?')[0];
  const segments = pathPart.split('/');
  return segments[segments.length - 1] || null;
};

export const handleParamLogic = (
  queries: Record<string, string>,
  url?: string
) => {
  if (queries?.externalPayment) {
    usePaymentStore.getState().setExternalPaymentFlowCompleted(true);
  }
  if (!queries?.userId || !url) return;

  const paymentMethod = extractPaymentMethod(url);
  const isMobilePay = paymentMethod === 'MOBILEPAY';

  if (!isMobilePay) return;

  const status = queries.status || '100';

  switch (status) {
    case '100':
      handleMobilePayVerification(queries.userId, status, paymentMethod);
      break;
    case '202':
      handleMobilePayCancellation('MOBILEPAY', 'AIMO_ONE_FI');
      break;
    default:
      break;
  }
};

export const readQueryParams = (url: string) => {
  const [, queryString] = url.split('?');
  const queries = queryString
    ?.split('&')
    .reduce((params: Record<string, string>, param: string) => {
      const [key, value] = param.split('=').map(decodeURIComponent);
      params[key] = value;
      return params;
    }, {});
  handleParamLogic(queries, url);
};

export const findNavigatorWithRoute = (navigation: any, routeName: string) => {
  let nav = navigation;

  while (nav?.getParent && !nav.getState?.()?.routeNames?.includes(routeName)) {
    nav = nav.getParent();
  }

  return nav;
};
